import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { DataEntryUser } from '../types/dataEntryUser';

const COLLECTION_NAME = 'calendars-users';

const toDateValue = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeUser = (raw: any): DataEntryUser => ({
  id: raw.id,
  userId: String(raw.userId || ''),
  name: String(raw.name || ''),
  avatar: String(raw.avatar || ''),
  isAdmin: Boolean(raw.isAdmin),
  dataEntryRole: Boolean(raw.dataEntryRole),
  createdAt: toDateValue(raw.createdAt),
  updatedAt: toDateValue(raw.updatedAt),
  updatedBy: raw.updatedBy || '',
});

export const dataEntryUserService = {
  async getUserByUserId(userId: string): Promise<DataEntryUser | null> {
    const q = query(collection(db, COLLECTION_NAME), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const first = snapshot.docs[0];
    return normalizeUser({ id: first.id, ...first.data() });
  },

  async getAllUsers(): Promise<DataEntryUser[]> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeUser({ id: item.id, ...item.data() }));
  },

  async updateUserByUserId(userId: string, partial: Partial<DataEntryUser>): Promise<void> {
    const q = query(collection(db, COLLECTION_NAME), where('userId', '==', userId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('Không tìm thấy người dùng để cập nhật quyền.');
    }

    const target = snapshot.docs[0];
    await updateDoc(doc(db, COLLECTION_NAME, target.id), {
      ...partial,
      updatedAt: new Date(),
    });
  },

  async addUser(user: DataEntryUser): Promise<DataEntryUser> {
    const payload = {
      ...user,
      createdAt: user.createdAt || new Date(),
      updatedAt: user.updatedAt || new Date(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), payload);
    return normalizeUser({ id: docRef.id, ...payload });
  },
};
